import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { switchMap, take } from 'rxjs/operators';
import { DemoModeService } from '../services/demo-mode.service';
import { SystemAccessService } from '../services/system-access.service';
import { TierService } from '../services/tier.service';
import type { OverCap } from '../models/billing.model';

/** The step's own address. */
const STEP = '/admin/plan-fit';
/** The way up stays open: the two billing pages a brand over its plan can always reach. */
const WAY_UP: ReadonlySet<string> = new Set(['/admin/settings/plan-usage', '/admin/settings/payment-invoices']);
/** The Members page — open when the owners alone exceed the cap (an owner can leave from their account; nothing on the step can be chosen). */
const MEMBERS = '/admin/members';

/**
 * While a brand holds more active people or more content languages than its plan includes, every admin
 * route opens on the fit step — the page where an admin chooses who and which languages stay — except
 * Plan and usage and Payment and invoices (the way up stays open) and, when the owners alone exceed the
 * cap, the Members page. The guard sits on the admin node's CHILDREN, so it runs for every admin page the
 * router activates and never for the public refusal twin, which copies the node's path alone.
 *
 * It answers for the step's own audience: a caller below Admin is never redirected (a members-manager, a
 * monitoring root without the privilege, a paused member — the step is the admin's to answer, and each
 * page's own guard decides the rest), an anonymous caller passes without any read (the page's own guard
 * sends them to sign in), and demo mode passes at once.
 *
 * On a cold load the plan state is unloaded when a guard runs, so the guard awaits the FIRST answer — one
 * shared request for the whole navigation, however many children it activates — and a load that fails
 * lets the route through: the server's own gates still refuse what such a brand may not do. A CACHED
 * "over" may be stale (a choice made on the step, a member who left since the last read), so before
 * anyone is sent to the step on a cached answer the plan state is re-read once; a re-read that fails
 * leaves the last known answer standing, and the step re-reads for itself. The step itself admits only
 * while the brand is over: opened by bookmark on a brand that fits, it lands on the Members page.
 */
export const planFitGuard: CanActivateChildFn = (_child, state) => {
  if (inject(DemoModeService).isDemo()) {
    return true;
  }
  const access = inject(SystemAccessService);
  const tier = inject(TierService);
  const router = inject(Router);
  // Both evaluations of a nested path (the settings page and its tab) answer alike: the address is the whole URL's.
  const path = state.url.split('?')[0];

  return inject(OidcSecurityService).isAuthenticated$.pipe(
    take(1),
    switchMap(async ({ isAuthenticated }) => {
      if (!isAuthenticated || !(await access.isAdmin())) {
        return true;
      }

      const wasLoaded = tier.isLoaded();
      try {
        await tier.ensureLoaded();
      } catch (error) {
        console.warn('[PlanFit] The plan state did not load before an admin page; the page opens and the server decides.', error);
        return true;
      }
      let over = tier.overCaps();
      if (wasLoaded && over.length > 0 && !admitsWhileOver(path, over)) {
        // The only case a cached answer would send someone to the step: re-read once first. The step itself, the way
        // up and the owners' Members page admit on the cached answer (the step re-reads for itself), so the guard's
        // own redirect never asks twice.
        try {
          await tier.refresh();
        } catch (error) {
          console.warn('[PlanFit] The plan state could not be re-read before the step; the last answer stands.', error);
        }
        over = tier.overCaps();
      }

      if (over.length === 0) {
        return path === STEP ? router.createUrlTree([MEMBERS]) : true;
      }
      return admitsWhileOver(path, over) ? true : router.createUrlTree([STEP]);
    }),
  );
};

/** The admin addresses a brand over its plan may still open: the step, the way up, and Members when the owners alone exceed. */
function admitsWhileOver(path: string, over: readonly OverCap[]): boolean {
  return path === STEP || WAY_UP.has(path) || (path === MEMBERS && ownersAloneExceed(over));
}

/** The members row's locked part — the owners — above its limit: nothing on the step can be chosen. */
function ownersAloneExceed(over: readonly OverCap[]): boolean {
  const members = over.find(row => row.kind === 'MEMBERS');
  return members !== undefined && members.locked > members.limit;
}
